/**
 * NexusClinicianProfile - Provider directory & Nexus profile editor
 * Edit service lines, payers, modalities, age range, languages, caseload, geography
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Save, UserCog, CheckCircle2, XCircle, ChevronDown, ChevronUp } from "lucide-react";

const SERVICE_OPTIONS = ["BA", "IIC", "BPS", "FCT", "PHP", "IOP"];
const SPECIALTY_OPTIONS = ["trauma", "autism", "adhd", "depression", "anxiety", "substance_abuse", "bipolar", "eating_disorders", "conduct_disorders", "attachment", "behavioral"];
const PAYER_OPTIONS = ["PerformCare NJ", "Aetna", "Horizon BCBS", "United Healthcare", "Cigna", "AmeriHealth", "Medicaid FFS"];
const MODALITY_OPTIONS = ["in_person", "telehealth", "home_based", "school_based"];
const LANGUAGE_OPTIONS = ["English", "Spanish", "Portuguese", "Mandarin", "Hindi", "Arabic", "French", "Haitian Creole"];
const NJ_COUNTIES = [
  "Atlantic","Bergen","Burlington","Camden","Cape May","Cumberland",
  "Essex","Gloucester","Hudson","Hunterdon","Mercer","Middlesex",
  "Monmouth","Morris","Ocean","Passaic","Salem","Somerset","Sussex",
  "Union","Warren"
];

const NexusClinicianProfile = ({ token, API }) => {
  const [clinicians, setClinicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [editState, setEditState] = useState({});
  const [saving, setSaving] = useState(null);

  const fetchClinicians = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/nexus/clinicians/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setClinicians(data.data || []);
    } catch (err) {
      toast.error("Failed to load clinicians");
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  useEffect(() => { fetchClinicians(); }, [fetchClinicians]);

  const toggleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(id);
    const c = clinicians.find(cl => cl.id === id);
    const p = c?.nexus_profile || {};
    setEditState({
      service_lines: p.service_lines || [],
      specialties: p.specialties || [],
      accepted_payers: p.accepted_payers || [],
      modalities: p.modalities || [],
      languages: p.languages || ["English"],
      age_range: p.age_range || { min: 5, max: 21 },
      max_caseload: p.max_caseload || 25,
      accepting_new: p.accepting_new !== false,
      service_counties: p.service_counties || [],
      npi: p.npi || "",
    });
  };

  const toggleArrayItem = (field, value) => {
    setEditState(prev => {
      const arr = prev[field] || [];
      return { ...prev, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
    });
  };

  const handleSave = async (userId) => {
    setSaving(userId);
    try {
      const res = await fetch(`${API}/api/nexus/clinicians/${userId}/nexus-profile`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(editState),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Profile saved");
        fetchClinicians();
      } else toast.error(data.detail || "Save failed");
    } catch (err) {
      toast.error("Network error");
    } finally {
      setSaving(null);
    }
  };

  const ChipSelector = ({ label, options, field }) => (
    <div>
      <Label className="text-xs font-medium text-slate-600">{label}</Label>
      <div className="flex flex-wrap gap-1.5 mt-1">
        {options.map(opt => {
          const selected = (editState[field] || []).includes(opt);
          return (
            <button
              key={opt}
              type="button"
              className={`px-2 py-0.5 rounded-full text-[11px] border transition-all ${
                selected ? "bg-indigo-100 text-indigo-700 border-indigo-300" : "bg-white text-slate-500 border-slate-200 hover:border-indigo-200"
              }`}
              onClick={() => toggleArrayItem(field, opt)}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;
  }

  return (
    <div data-testid="nexus-clinician-profiles" className="space-y-3">
      <p className="text-sm text-slate-500">{clinicians.length} clinician{clinicians.length !== 1 ? "s" : ""} in directory</p>

      {clinicians.map(c => {
        const hasProfile = !!c.nexus_profile;
        const expanded = expandedId === c.id;

        return (
          <Card key={c.id} data-testid={`clinician-card-${c.id}`} className="border-slate-200">
            <CardContent className="py-3 px-4">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => toggleExpand(c.id)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${hasProfile ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-400"}`}>
                    <UserCog className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{c.full_name || "Unknown"}</p>
                    <p className="text-xs text-slate-500">{c.role} &middot; {c.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {hasProfile ? (
                    <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]">
                      <CheckCircle2 className="w-3 h-3 mr-0.5" /> Profile Set
                    </Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-500 border-0 text-[10px]">
                      <XCircle className="w-3 h-3 mr-0.5" /> No Profile
                    </Badge>
                  )}
                  {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </div>
              </div>

              {/* Expanded Editor */}
              {expanded && (
                <div className="mt-4 pt-4 border-t border-slate-100 space-y-4">
                  <ChipSelector label="Service Lines" options={SERVICE_OPTIONS} field="service_lines" />
                  <ChipSelector label="Specialties" options={SPECIALTY_OPTIONS} field="specialties" />
                  <ChipSelector label="Accepted Payers" options={PAYER_OPTIONS} field="accepted_payers" />
                  <ChipSelector label="Modalities" options={MODALITY_OPTIONS} field="modalities" />
                  <ChipSelector label="Languages" options={LANGUAGE_OPTIONS} field="languages" />
                  <ChipSelector label="Service Counties" options={NJ_COUNTIES} field="service_counties" />

                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Min Age</Label>
                      <Input
                        type="number"
                        value={editState.age_range?.min || 0}
                        onChange={(e) => setEditState(prev => ({ ...prev, age_range: { ...prev.age_range, min: parseInt(e.target.value) || 0 } }))}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Max Age</Label>
                      <Input
                        type="number"
                        value={editState.age_range?.max || 99}
                        onChange={(e) => setEditState(prev => ({ ...prev, age_range: { ...prev.age_range, max: parseInt(e.target.value) || 99 } }))}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Max Caseload</Label>
                      <Input
                        type="number"
                        value={editState.max_caseload || 25}
                        onChange={(e) => setEditState(prev => ({ ...prev, max_caseload: parseInt(e.target.value) || 25 }))}
                        className="h-8 text-xs"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editState.accepting_new}
                        onCheckedChange={(v) => setEditState(prev => ({ ...prev, accepting_new: v }))}
                      />
                      <Label className="text-xs">Accepting New Clients</Label>
                    </div>
                    <div className="w-40">
                      <Label className="text-xs">NPI</Label>
                      <Input
                        value={editState.npi || ""}
                        onChange={(e) => setEditState(prev => ({ ...prev, npi: e.target.value }))}
                        className="h-8 text-xs"
                        placeholder="NPI number"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={() => handleSave(c.id)}
                      disabled={saving === c.id}
                      data-testid={`save-profile-${c.id}`}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      {saving === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                      Save Profile
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default NexusClinicianProfile;
